
import { Avatar, Image as AmplifyImage, useAuthenticator } from "@aws-amplify/ui-react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { TextAreaField, Loader, Button, Alert } from "@aws-amplify/ui-react";
import { Card } from "@aws-amplify/ui-react";
import { Placeholder } from "@aws-amplify/ui-react";
import { Group, Panel, Separator } from 'react-resizable-panels';
import Plot from "react-plotly.js";
import { useEffect, useMemo, useState, useRef } from "react";
import { apiGetPosts, apiGetPostsByUser, apiGetTimeline, apiPublishComment, apiPublishPost, apiSyncPosts } from "../api/api";
import { appendPosts, setLastSync, setPosts, insertPost, setAvatarError, deletePost } from "../store/postsSlice";
import { WorkoutSummary } from "./components/workout-summary";
import { PostDetailsCard } from "./components/post-details-card";
import { fetchUserAttributes } from "aws-amplify/auth";
import { insertComment } from "../store/commentsSlice";
import { CloseButton, OverlayTrigger } from "react-bootstrap";
import { Tooltip } from "react-bootstrap";
import { TopMoversCard } from "./components/top-movers-card";
import { Modal } from "react-bootstrap";
import { debounce } from 'lodash';


const DebouncePlot = ({ useResizeHandler, ...props }) => {
    const [key, setKey] = useState(0);

    useEffect(() => {
        if (!useResizeHandler) return;
        const debouncedResize = debounce(() => {
            setKey(prev => prev + 1);
        }, 100);
        window.addEventListener('resize', debouncedResize);
        return () => {
            window.removeEventListener('resize', debouncedResize);
            debouncedResize.cancel();
        };
    }, [useResizeHandler]);

    return <Plot key={key} useResizeHandler={false} {...props} />;
}
const colors = ['red', 'blue', 'green', 'orange', 'cyan', 'yellow']

const HomePage = ({ }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const hasFetched = useRef(false)
    const { user } = useAuthenticator((context) => [context.user]);
    const { exercisesLoading } = useOutletContext((context) => {
        return context.exercisesLoading
    })

    const params = useParams();
    const [postContent, setPostContent] = useState("")
    const favorites = useSelector((state) => {
        const favs = state.exercises?.items?.filter((value, index) => value.favorite === true)
        return favs || []
    }, shallowEqual)
    //const favorites = useSelector(selectFavorites)
    const results = useSelector((state) => {
        return state.results?.items
    })
    const avatarErrors = useSelector((state) => state.posts?.avatarErrors)

    const points = useMemo(() => {
        if (!favorites || !results) return [];

        return favorites.slice(0, 5).map((value, index) => {

            if (results[value?.id]?.items) {
                const grouped = Object.groupBy(results[value?.id]?.items, (item) => {
                    const date = new Date(item.timestamp);
                    return date.toISOString().split('T')[0]
                });
                const x = [];
                const y = [];
                const sortedEntries = Object.entries(grouped).sort((a, b) =>
                    new Date(a[0]) - new Date(b[0])
                );
                for (let [key, value] of sortedEntries) {
                    if (value.size === 0) continue
                    const date = new Date(key);
                    x.push(date);
                    const maxObject = value?.reduce((max, item) =>
                        item.result > max.result ? item : max
                    );
                    y.push(maxObject.result);
                }
                return (
                    {
                        x: x,
                        y: y,
                        hovertemplate: "<b>Date:</b> %{x|%b %d}<br>" +
                            "<b>Value:</b> %{y:.1f} kg<br>" +
                            "<extra></extra>",
                        type: 'scatter',
                        mode: 'lines+markers',
                        marker: { color: colors[index] },
                    }
                )
            } else return null
        }).filter(item => item !== null); // Remove null entries
    }, [favorites, results]);

    const posts = useSelector((state) => state?.posts?.items)
    const lastSync = useSelector((state) => state?.posts?.lastSync ?? 0)
    const [alert, setAlert] = useState(null)
    const [error, setError] = useState(null)
    const [userName, setUserName] = useState("")
    const [postToDelete, setPostToDelete] = useState(null)
    const [deletionInProgress, setDeletionInProgress] = useState(false)
    const [loadingPosts, setLoadingPosts] = useState(false)
    const [postIsBeingPublished, setPostIsBeingPublished] = useState(false)
    const [postImage, setPostImage] = useState({
        fileName: "",
        fullFileName: "",
        file: null
    })
    const apiEnabled = useSelector(state => state.exercises?.apiEnabled)
    const processImage = (file) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            img.onload = () => {
                // Calculate scaled dimensions while preserving aspect ratio
                let width = img.width;
                let height = img.height;

                if (width > 1280 || height > 1280) {
                    if (width > height) {
                        // Landscape or square: scale by width
                        const scaleFactor = 1280 / width;
                        width = 1280;
                        height = Math.floor(height * scaleFactor);
                    } else {
                        // Portrait: scale by height
                        const scaleFactor = 1280 / height;
                        height = 1280;
                        width = Math.floor(width * scaleFactor);
                    }
                }

                // Set canvas to calculated dimensions
                canvas.width = width;
                canvas.height = height;

                // Draw scaled image
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to data URL
                const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
                resolve(dataUrl);
            };

            img.onerror = reject;
            img.src = URL.createObjectURL(file);
        });
    };

    const fetchPosts = async () => {
        try {
            setLoadingPosts(true)
            const myPostsResult = await apiGetPostsByUser(user.userId)
            dispatch(setPosts(myPostsResult.data?.posts))
            const result = await apiGetTimeline(user.userId, lastSync)
            const timeline = result.data?.timeline
            dispatch(setLastSync({
                lastSync: result.data?.time
            }))

            const postsToFetch = timeline?.filter((e) => e.author !== user.userId)?.map((e) => ({
                author: e.author,
                timestamp: e.timestamp
            }))
            if (postsToFetch.length === 0) return;
            const postsResult = await apiGetPosts(postsToFetch)
            const posts = postsResult.data?.posts
            dispatch(appendPosts(posts))
        } catch (e) {
            setError("Network error in apiGetPosts")
            console.log(e);
        } finally {
            setLoadingPosts(false)
        }
    }
    const getUserName = async () => {
        const attributes = await fetchUserAttributes()
        setUserName(attributes?.name ?? "")
    }

    useEffect(() => {
        if (!hasFetched.current && apiEnabled) {
            hasFetched.current = true
            getUserName()
            if (posts.length === 0)
                fetchPosts()


        }
    }, [user.userId])

    const [leftPanelSize, setLeftPanelSize] = useState(400);

    return (
        <div>
            <Group direction="horizontal" >
                <Panel defaultSize={500}
                    className="pt-1"
                    minSize={500}
                    onResize={(it) => {
                        //it contains asPercentage and inPixels
                        setLeftPanelSize(it.inPixels)
                    }}
                    maxSize={800} style={{
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}>
                    <Card
                        variation="elevated">
                        <h2>Favorites</h2>
                        {(!favorites || exercisesLoading) &&
                            <Placeholder className="w-100" style={{ minHeight: "20rem" }}>

                            </Placeholder>
                        }
                        {favorites && !exercisesLoading &&
                            <div className="d-flex justify-content-center">
                                <DebouncePlot
                                    style={{ display: "block", width: `${leftPanelSize}px`, position: 'relative', height: '400px' }}
                                    data={points}
                                    layout={{
                                        autosize: true,
                                        showlegend: false,
                                        yaxis: {
                                            autorange: true,
                                            side: 'left',
                                            tickformat: ".0f",
                                            title: {
                                                text: "Max",
                                                font: {
                                                    size: 12
                                                }
                                            }
                                        },
                                        xaxis: {
                                            type: 'date',
                                            tickformat: '%b %d',
                                            hoverformat: '%b %d',
                                            title: {
                                                text: 'Date',
                                                font: { size: 12 }
                                            }
                                        },
                                        margin: {
                                            l: 60, // Increased left margin for y-axis title
                                            r: 30,
                                            t: 50,
                                            b: 70
                                        }
                                    }}
                                    config={{
                                        responsive: true,
                                        displayModeBar: false
                                    }}

                                    useResizeHandler={false}
                                />
                            </div>
                        }
                    </Card>
                    <Card
                        className="mt-2"
                        variation="elevated">
                        {exercisesLoading &&
                            <div className="d-flex align-items-center justify-content-center">
                                <Loader></Loader>
                                <span className="ms-1">Loading</span>
                            </div>}
                        {!favorites &&
                            <Placeholder className="w-100" style={{ minHeight: "20rem" }}>
                            </Placeholder>}
                        {
                            favorites?.length === 0 &&
                            <div>
                                <Link to="/exercises">
                                    Create an exercise and add it to your favorites list
                                </Link>
                            </div>
                        }
                        {favorites &&
                            <div className="d-flex justify-content-center">
                                <div className="mx-3 my-1 d-flex flex-column" style={{ width: "fit-content" }}>
                                    {favorites?.sort((a, b) => b.name.length - a.name.length).slice(0, 5).map((value, index) => (
                                        <div className="d-flex align-items-center justify-content-center" key={index}>
                                            <div style={{
                                                backgroundColor: colors[index],
                                                width: "10px",
                                                height: "10px"
                                            }}>
                                            </div>
                                            <span key={index} className="mx-1">{value.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        }
                    </Card>
                </Panel>
                <Separator style={{
                    width: '8px',
                    backgroundColor: '#e0e0e0',
                    cursor: 'col-resize',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        width: '4px',
                        height: '40px',
                        backgroundColor: '#999',
                        borderRadius: '4px'
                    }} />
                </Separator>
                <Panel style={{ overflowY: 'auto', overflowX: 'hidden' }} className="pe-1 pt-1">
                    {!params.id &&
                        <Card style={{ minHeight: "35rem" }}
                            variation="elevated">
                            <Card variation="elevated" className="text-align-start">
                                <TextAreaField
                                    size="small"
                                    descriptiveText="Publish a new post"
                                    placeholder="Tell your Buddies about your workout"
                                    value={postContent}
                                    onChange={(e) => {
                                        setPostContent(e.target.value)
                                    }
                                    }
                                    autoResize
                                    errorMessage="There is an error"
                                />
                                <div className="d-flex justify-content-between w-100 align-items-center">
                                    <div >
                                        {postImage.fullFileName && <div className="d-flex align-items-center">
                                            <CloseButton onClick={() => {
                                                setPostImage({
                                                    fileName: "",
                                                    fullFileName: "",
                                                    file: null
                                                })
                                            }
                                            } /> <span className="ms-2"> {postImage.fileName} </span>
                                        </div>}
                                    </div>
                                    <div className="d-flex mt-2 justify-content-end">
                                        <input type="file" hidden id="picture-selector" value={postImage.fullFileName} accept="image/*"
                                            onChange={(e) => {
                                                setPostImage({
                                                    fileName: e.target.files[0]?.name,
                                                    fullFileName: e.target.value,
                                                    file: e.target.files[0]
                                                })
                                            }}
                                        />
                                        <Button
                                            onClick={() => {
                                                const picker = document.getElementById("picture-selector")
                                                if (picker) {
                                                    picker.click()
                                                }
                                            }}
                                            className="me-2">
                                            <span className="material-symbols-outlined">
                                                image_arrow_up
                                            </span>
                                        </Button>
                                        <Button
                                            size="small"
                                            loadingText="Wait.."
                                            isLoading={postIsBeingPublished}
                                            isDisabled={postIsBeingPublished || postContent?.length === 0}
                                            onClick={async () => {
                                                setPostIsBeingPublished(true)
                                                try {
                                                    let imageUrl = ""
                                                    if (postImage.file)
                                                        imageUrl = await processImage(postImage.file)

                                                    const postToPublish = {
                                                        user: user.userId,
                                                        timestamp: Date.now(),
                                                        author: user.userId,
                                                        authorName: userName,
                                                        comments: 0,
                                                        content: postContent,
                                                        deleted: false,
                                                        id: crypto.randomUUID(),
                                                        imageUrl: imageUrl,
                                                        likes: 0,
                                                        modified: Date.now(),
                                                        payload: '{"topMovers":[],"workoutSummary":null}'
                                                    }
                                                    const postPublishResult = await apiPublishPost(postToPublish)
                                                    if (postPublishResult?.data?.message === "ok") {
                                                        setAlert("Post has been published")

                                                        dispatch(insertPost({
                                                            post: postToPublish
                                                        }))
                                                    }

                                                } catch (e) {
                                                    setError("Network error in apiPublishPost")
                                                }
                                                finally {
                                                    setPostIsBeingPublished(false)
                                                    setPostContent("")
                                                    setPostImage({
                                                        fileName: "",
                                                        fullFileName: "",
                                                        file: null
                                                    })
                                                }
                                            }}
                                        >
                                            Publish
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                            {loadingPosts &&
                                <div className="d-flex justify-content-center mt-3">
                                    <Loader /> <span className="ms-2">Loading...</span>
                                </div>
                            }
                            {
                                posts?.length === 0 &&
                                <div className="mt-4">
                                    <p className="m-2">
                                        You have no publications to display. Publish your first post or
                                    </p>
                                    <Button as={Link} to="/buddies">
                                        Find your Buddies
                                    </Button>
                                    <p className="m-2">
                                        to see what they've posted.
                                    </p>
                                </div>
                            }

                            {posts?.filter((item, index) => item.deleted === false)?.map((post, index) => {
                                let payload = null
                                try {
                                    payload = JSON.parse(post?.payload)

                                } catch (e) {
                                    payload = null
                                }
                                return (
                                    <div key={index}>
                                        {postToDelete?.id !== post.id &&
                                            <Card
                                                onClick={() => {
                                                    navigate(`/posts/${post?.id}`)
                                                }}
                                                className="text-align-start mt-2 clickable"
                                                key={index}
                                                variation="elevated">
                                                <div className="d-flex justify-content-start">
                                                    {!avatarErrors[post?.author] &&
                                                        <AmplifyImage className="post-avatar"
                                                            src={`https://liftolog-media.s3.eu-central-1.amazonaws.com/avatars/${post?.author}.jpg`}
                                                            onError={() => {
                                                                dispatch(setAvatarError({
                                                                    author: post?.author
                                                                }))
                                                            }}
                                                        />
                                                    }
                                                    {
                                                        avatarErrors[post?.author] &&
                                                        <Avatar className="post-avatar" />
                                                    }
                                                    <div className="w-100">
                                                        <div className="d-flex justify-content-between w-100">
                                                            <h5 >{post?.authorName}</h5>
                                                            {post?.author === user.userId &&
                                                                <CloseButton
                                                                    disabled={deletionInProgress}
                                                                    onClick={(e) => {
                                                                        setPostToDelete(post)
                                                                        e.stopPropagation()
                                                                    }} />
                                                            }
                                                        </div>
                                                        <p className="small"> {(new Date(post?.timestamp)).toDateString()}</p>
                                                    </div>
                                                </div>
                                                <hr></hr>
                                                {post?.imageUrl &&
                                                    <AmplifyImage src={post?.imageUrl}>
                                                    </AmplifyImage>
                                                }
                                                <p>{post?.content}</p>
                                                <div className="d-flex w-100 justify-content-between">
                                                    <span>{`Comments ${post?.comments}`}</span>
                                                    <OverlayTrigger placement="left" overlay={<Tooltip id={`likes-tooltip-${post.id}`}>Open Post Details to control Likes</Tooltip>}>
                                                        {({ ref, ...triggerHandler }) => (
                                                            <div className="d-flex" {...triggerHandler} ref={ref}>
                                                                <span className="material-symbols-outlined">
                                                                    thumb_up
                                                                </span>
                                                                <span className="ms-2">
                                                                    {post?.likes}
                                                                </span>
                                                            </div>
                                                        )
                                                        }
                                                    </OverlayTrigger>
                                                </div>
                                                {payload?.workoutSummary?.exercises?.length &&
                                                    <div>
                                                        <h6 className="mt-2 text-align-center" >{`Workout Summary ${payload?.workoutSummary?.date}`}</h6>
                                                        <WorkoutSummary
                                                            workout={payload?.workoutSummary?.exercises}>
                                                        </WorkoutSummary>
                                                    </div>}
                                                {payload?.topMovers?.length !== 0 &&
                                                    <TopMoversCard topMovers={payload?.topMovers} />
                                                }
                                            </Card>
                                        }
                                        {
                                            postToDelete?.id === post.id &&
                                            <Placeholder style={{ height: '200px' }} />
                                        }
                                    </div>
                                )

                            }
                            )}
                        </Card>}
                    {
                        params.id &&
                        <PostDetailsCard
                            postId={params.id}
                            onPublishComment={async (content) => {
                                try {
                                    const commentToPublish = {
                                        user: user.userId,
                                        timestamp: Date.now(),
                                        author: user.userId,
                                        authorName: userName,
                                        content: content,
                                        deleted: false,
                                        id: crypto.randomUUID(),
                                        modified: Date.now(),
                                        postId: params.id
                                    }
                                    const commentPublishResult = await apiPublishComment(commentToPublish)
                                    if (commentPublishResult?.data?.message === "ok") {
                                        setAlert("Comment has been published")
                                        dispatch(insertComment({
                                            postId: params.id,
                                            comment: commentToPublish
                                        }))
                                    }

                                } catch (e) {
                                    setError("Network error in apiPublishComment")
                                }
                            }}
                            user={user.userId}
                            userName={userName}
                            onError={(e) => { setError(e) }}
                        ></PostDetailsCard>
                    }
                </Panel>
            </Group>
            {error &&
                <Alert
                    className="alert"
                    variation="error"
                    isDismissible={true}
                    hasIcon={true}
                    onDismiss={() => {
                        setError(null)
                    }}
                    heading="Error"
                >
                    {error}
                </Alert>
            }
            {alert &&
                <Alert
                    className="alert"
                    variation="success"
                    isDismissible={true}
                    hasIcon={true}
                    onDismiss={() => {
                        setAlert(null)
                    }}
                    heading="Success"
                >
                    {alert}
                </Alert>
            }
            <Modal
                show={postToDelete !== null}
                onHide={() => { setPostToDelete(null) }}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Post?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the post?
                </Modal.Body>
                <Modal.Footer>
                    <Button className="m-1" variation="default" onClick={() => { setPostToDelete(null) }}>
                        Cancel
                    </Button>
                    <Button className="m-1" variation="primary" colorTheme="warning" onClick={async () => {
                        try {
                            const _postToDelete = { ...postToDelete }
                            _postToDelete.deleted = true
                            _postToDelete.modified = Date.now()
                            setDeletionInProgress(true)
                            const syncPostResponse = await apiSyncPosts([_postToDelete], user.userId)
                            if (syncPostResponse.data?.message === "ok") {
                                dispatch(deletePost(
                                    {
                                        postId: _postToDelete.id,
                                    }
                                ))
                            }
                        } catch (e) {
                            setError("Network error in apiSyncPosts")
                            console.log(e)
                        } finally {
                            setDeletionInProgress(false)
                        }
                        setPostToDelete(null)
                    }}>Yes, delete</Button>
                </Modal.Footer>
            </Modal>
        </div>
    )

}

export default HomePage;